/**
 * A custom type guard that checks for types which can have custom properties (see https://www.typescriptlang.org/docs/handbook/advanced-types.html#user-defined-type-guards)
 * Note that this basically checks if something is an Object, Array (instance of Object), or Function
 * null, undefined, Symbol, string, and number are all incapable of having custom properties added
 * Any missing types should be considered and documented here
 * Created primarily to simplify the creation of other custom type guards
 * @param thing Thing which is being type guarded
 * @returns Whether the thing can have custom properties
 * @example
 * interface Pet {
 *   type: 'dog' | 'cat',
 *   firstName: string,
 *   age: number,
 * }
 * function isPet(thing: unknown): thing is Pet {
 *   return canHaveCustomProperties(thing)
 *     && (thing.type === 'cat' || thing.type === 'cat')
 *     && typeof thing.firstName === 'string
 *     && typeof thing.age === 'number'
 * }
 * isPet({ type: 'dog', firstName: 'Oliver', age: 0.79 }) // returns true
 * isPet({ type: 'dog', firstName: 'Oliver', age: '0.79' }) // returns false
 * isPet({ type: 'dog', firstName: 'Oliver' }) // returns false
 * isPet({ type: 'snake', firstName: 'Oliver', age: 0.79 }) // returns false
 * const myArrayPet: any = [] // js allows adding custom props to an array, but typescript doesn't like it so it must be cast to `any`
 * myArrayPet.type = 'cat'
 * myArrayPet.firstName = 'Osiris'
 * myArrayPet.age = 3
 * isPet(myArrayPet) // returns true
 * const myFuncPet = function() {} // typescript allows add custom props to a constant function
 * myFuncPet.type = 'dog'
 * myFuncPet.firstName = 'Doggy'
 * myFuncPet.age = 22
 * isPet(myFuncPet) // returns true
 */
export function canHaveCustomProperties(thing: unknown): thing is Record<PropertyKey, unknown> {
  return (typeof thing === 'object' && thing !== null) || typeof thing === 'function'
}
